import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import {
  TextField,
  Checkbox,
  Datepicker,
  RadioButton,
  RadioButtonGroup,
  Label,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import useCubeMetaAPI from '../../../../../../../hooks/useCubeMetaAPI';
import DateRangeFilter from './DateRangeFilter';
import ReferenceValueField from './ReferenceValueField';

function ValueField({
  field,
  index,
  currentCubeMembers,
  disabled,
  fieldType,
  selectedMember,
  selectedOperator,
  formValues,
}) {
  const intl = useIntl();
  const { response: cubeMetaResponse } = useCubeMetaAPI();
  const cubeMetaData = { response: cubeMetaResponse };
  // cubejsApi removed - no longer used directly

  // Get data type for selected field
  const getFieldDataType = () => {
    if (!selectedMember || !currentCubeMembers) return 'string';

    // Handle selectedMember as object or string (after page refresh, it comes from URL as object)
    const memberName =
      typeof selectedMember === 'string'
        ? selectedMember
        : selectedMember?.value || selectedMember;
    if (!memberName || typeof memberName !== 'string') return 'string';

    for (const member of currentCubeMembers) {
      const fields = member[fieldType] || [];
      const foundField = fields.find((f) => f.name === memberName);
      if (foundField) {
        return foundField.type || 'string';
      }
    }
    return 'string';
  };

  const dataType = getFieldDataType();

  // Check if this is a reference field
  const isReferenceField = () => {
    if (!selectedMember) return false;

    const memberName =
      typeof selectedMember === 'string'
        ? selectedMember
        : selectedMember?.value || selectedMember;
    if (!memberName || typeof memberName !== 'string') return false;

    const [cubeName] = memberName.split('.');

    // 1. Check currentCubeMembers — works for both ViewReportBuilder (aliasMember)
    //    and legacy selectors (view field names)
    if (currentCubeMembers) {
      const cube = currentCubeMembers.find((c) => c.name === cubeName);
      if (cube?.meta?.isReferenceTable === true) return true;
    }

    // 2. Fallback to cubeMetaData — for Advanced Query Builder
    if (cubeMetaData?.response?.cubes) {
      const cube = cubeMetaData.response.cubes.find((c) => c.name === cubeName);
      if (cube?.meta?.isReferenceTable === true) return true;
    }

    return false;
  };

  // Render appropriate component based on data type and selected operator
  const renderValueInput = () => {
    const commonProps = {
      id: `query-builder-form-valueField-${index}`,
      name: `${field}.values`,
      disabled,
    };

    // No value field needed for set/notSet operations
    if (['set', 'notSet'].includes(selectedOperator)) {
      return null;
    }

    // Use DateRangeFilter for date range operations
    if (['inDateRange', 'notInDateRange'].includes(selectedOperator)) {
      return (
        <DateRangeFilter field={field} index={index} disabled={disabled} />
      );
    }

    // Check if this is a reference field first
    if (isReferenceField()) {
      return (
        <ReferenceValueField
          field={field}
          index={index}
          disabled={disabled}
          selectedMember={selectedMember}
          currentCubeMembers={currentCubeMembers}
        />
      );
    }

    switch (dataType) {
      case 'time':
        return (
          <Field
            {...commonProps}
            component={Datepicker}
            label={
              <FormattedMessage
                id="ui-reports.filters.valueField.label"
                defaultMessage="Value"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-reports.filters.valueField.selectPlaceholder',
              defaultMessage: 'Select value',
            })}
            backendDateStandard="ISO8601"
          />
        );

      case 'boolean':
        return (
          <>
            <Label>
              <FormattedMessage
                id="ui-reports.filters.valueField.label"
                defaultMessage="Value"
              />
            </Label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Field
                {...commonProps}
                component={RadioButton}
                type="radio"
                label={intl.formatMessage({
                  id: 'ui-reports.filters.valueField.boolean.yes',
                  defaultMessage: 'Yes',
                })}
                value="true"
              />
              <Field
                {...commonProps}
                component={RadioButton}
                type="radio"
                label={intl.formatMessage({
                  id: 'ui-reports.filters.valueField.boolean.no',
                  defaultMessage: 'No',
                })}
                value="false"
              />
            </div>
          </>
        );

      case 'number':
        return (
          <Field
            {...commonProps}
            component={TextField}
            type="number"
            label={
              <FormattedMessage
                id="ui-reports.filters.valueField.label"
                defaultMessage="Value"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-reports.filters.valueField.placeholder',
              defaultMessage: 'Enter value',
            })}
          />
        );

      default: // string
        return (
          <Field
            {...commonProps}
            component={TextField}
            label={
              <FormattedMessage
                id="ui-reports.filters.valueField.label"
                defaultMessage="Value"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-reports.filters.valueField.placeholder',
              defaultMessage: 'Enter value',
            })}
          />
        );
    }
  };

  return renderValueInput();
}

ValueField.propTypes = {
  field: PropTypes.string.isRequired,
  fieldType: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  currentCubeMembers: PropTypes.array.isRequired,
  disabled: PropTypes.bool.isRequired,
  selectedMember: PropTypes.string,
  selectedOperator: PropTypes.string,
  formValues: PropTypes.object,
};

export default ValueField;
